import { SerializedNodes, useEditor } from '@craftjs/core';
import { useCallback } from 'react';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';

type ImportLayoutOptions = {
  onBeforeImport?: () => void;
};

/* This hook provides a function to import a layout from a JSON file.
It creates a file input element, reads the selected file, and uses the editor's deserialize action
to load the layout. The function returns a promise that resolves when the import is successful
or rejects if there's an error. */
const useLayoutImport = () => {
  const { t } = useScopedTranslation('LayoutImport');
  const { actions, query } = useEditor();

  const validateLayout = useCallback(
    (parsed: unknown): SerializedNodes => {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error(t('Errors.InvalidSchema'));
      }

      const layout = parsed as SerializedNodes;
      if (!layout.ROOT) {
        throw new Error(t('Errors.MissingRootNode'));
      }

      for (const [nodeId, node] of Object.entries(layout)) {
        try {
          query.parseSerializedNode(node).toNode((parsedNode) => {
            parsedNode.id = nodeId;
          });
        } catch (error) {
          throw new Error(t('Errors.InvalidNode', { nodeId }), { cause: error });
        }
      }

      return layout;
    },
    [query, t],
  );

  const importLayout = useCallback(
    (options?: ImportLayoutOptions) =>
      new Promise<void>((resolve, reject) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (event) => {
          const file = (event.target as HTMLInputElement).files?.[0];
          if (!file) {
            resolve();
            return;
          }

          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = e.target?.result as string;
              const parsed = JSON.parse(content) as unknown;
              const layout = validateLayout(parsed);
              options?.onBeforeImport?.();
              actions.deserialize(layout);
              resolve();
            } catch (error) {
              reject(error instanceof SyntaxError ? new Error(t('Errors.InvalidJson')) : error);
            }
          };
          reader.onerror = () => {
            reject(reader.error ?? new Error(t('Errors.FailedReadFile')));
          };
          reader.readAsText(file);
        };

        input.click();
      }),
    [actions, validateLayout, t],
  );

  return {
    importLayout,
  };
};

export default useLayoutImport;
